/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import RestartAlt from '@mui/icons-material/RestartAlt';
import moment from 'moment';
import LogicContext from '../../Context/LogicContext';
import { budgetService } from '../../services/budgetService';
import { GetBudgetListData, SetBudgetListData } from '../../store/budgetAtom';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import DelgroProgressBar from '../../generals/DelgroProgressBar';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { normalizeBudgetListPayload } from './budgetDataHelpers';

const columns = [
  {
    title: 'Ref.No',
    key: 'budget_ref_no',
    dataIndex: 'budget_ref_no',
    sorter: (a, b) => (a?.budget_ref_no ?? 0) - (b?.budget_ref_no ?? 0),
  },
  {
    title: 'Budget Name',
    key: 'budget_name',
    dataIndex: 'budget_name',
    sorter: (a, b) => (a?.budget_name ?? '').localeCompare(b?.budget_name ?? ''),
  },
  {
    title: 'Budget Term',
    key: 'budget_term_name',
    dataIndex: 'budget_term_name',
    sorter: (a, b) => (a?.budget_term_name ?? '').localeCompare(b?.budget_term_name ?? ''),
  },
  {
    title: 'Financial Year',
    key: 'financial_year_code',
    dataIndex: 'financial_year_name',
    sorter: (a, b) => (a?.financial_year_code ?? 0) - (b?.financial_year_code ?? 0),
    render: (value, record) => <span>{value || record?.financial_year_code || '-'}</span>,
  },
  {
    title: 'Created Date',
    key: 'created_date',
    dataIndex: 'created_date',
    sorter: (a, b) => (a?.created_date ?? '').localeCompare(b?.created_date ?? ''),
    render: record => <span> {record ? moment(record).format('YYYY-MM-DD') : '—'} </span>,
  },
  {
    title: 'Amount',
    key: 'amout',
    dataIndex: 'amout',
    sorter: (a, b) => (a?.amout ?? 0) - (b?.amout ?? 0),
    render: record => <span> {formatCustomNumber(record ?? 0)} </span>,
  },
];

function BudgetMainPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  var url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const logicContext = useContext(LogicContext);

  const setBudgetListData = SetBudgetListData();
  const getBudgetListData = GetBudgetListData();

  const budgetListPayload = useMemo(
    () => (Array.isArray(getBudgetListData) ? { budget_info: getBudgetListData, financial_year: [], budget_term: [] } : getBudgetListData),
    [getBudgetListData]
  );
  const [tableData, setTableData] = useState([]);
  const [itemSelected, setItemSelected] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [activeYear, setActiveYear] = useState('all');
  const [activeTerm, setActiveTerm] = useState('all');

  useEffect(() => {
    document.title = 'Budget';
    if (!budgetListPayload.budget_info.length) {
      getBudgetListFromApi();
    } else {
      setTableData(budgetListPayload.budget_info);
    }
  }, [budgetListPayload]);

  useEffect(() => {
    if (sessionStorage.getItem('reloadPage') === 'true') {
      sessionStorage.removeItem('reloadPage');
      getBudgetListFromApi();
    }
  }, []);

  const getBudgetListFromApi = async () => {
    try {
      const response = await budgetService.budgetList();
      const payload = normalizeBudgetListPayload(response?.data);

      setTableData(payload.budget_info);
      setBudgetListData(payload);
    } catch (err) {
      console.error(err);
      logicContext.showToast('Failed to load budget list', 'error');
    }
  };

  const yearList = useMemo(() => {
    if (budgetListPayload.financial_year.length) return budgetListPayload.financial_year;

    return [
      ...new Map(
        tableData
          .filter(item => item?.financial_year_code)
          .map(item => [
            item.financial_year_code,
            {
              financial_year_code: item.financial_year_code,
              financial_year_name: item.financial_year_name || String(item.financial_year_code),
            },
          ])
      ).values(),
    ].sort((a, b) => b.financial_year_code - a.financial_year_code);
  }, [budgetListPayload.financial_year, tableData]);

  const termList = useMemo(() => {
    if (budgetListPayload.budget_term.length) return budgetListPayload.budget_term;

    return [
      ...new Map(
        tableData
          .filter(item => item?.budget_term_id)
          .map(item => [
            item.budget_term_id,
            {
              budget_term_id: item.budget_term_id,
              budget_term_name: item.budget_term_name || String(item.budget_term_id),
            },
          ])
      ).values(),
    ].sort((a, b) => a.budget_term_id - b.budget_term_id);
  }, [budgetListPayload.budget_term, tableData]);

  const filteredData = useMemo(() => {
    let list = tableData;
    if (activeYear !== 'all') {
      list = list.filter(item => String(item?.financial_year_code) === String(activeYear));
    }
    if (activeTerm !== 'all') {
      list = list.filter(item => String(item?.budget_term_id) === String(activeTerm));
    }
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      list = list.filter(
        item =>
          item?.budget_name?.toLowerCase().includes(query) ||
          String(item?.budget_ref_no).includes(query) ||
          item?.financial_year_name?.toLowerCase().includes(query) ||
          item?.budget_term_name?.toLowerCase().includes(query)
      );
    }
    return list;
  }, [tableData, activeYear, activeTerm, searchText]);

  const totalAmount = useMemo(() => filteredData.reduce((sum, item) => sum + Number(item?.amout ?? 0), 0), [filteredData]);

  function selectedTableItems(itemList) {
    setItemSelected(itemList);
  }

  const addNewBudget = useCallback(() => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  }, [navigate, currModuleId, currModuleName, currUserMenu]);

  const OnItemCellClick = useCallback(
    budget => {
      const id = budget['budget_ref_no'];
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
    },
    [navigate, currModuleId, currModuleName, currUserMenu]
  );

  return (
    <>
      <section>
        <section className="justify-between bg-white px-2 md:flex">
          <div className="mt-1 flex items-center px-2">
            <Tooltip title="Reload">
              <RestartAlt className="cursor ml-2 mt-1" onClick={() => getBudgetListFromApi()} color="primary" fontSize="large" />
            </Tooltip>

            {itemSelected?.length > 0 && <span className="ml-3 text-sm text-gray-500">{itemSelected.length} selected</span>}
          </div>

          {/* search and financial year filter */}
          <div className="flex flex-row items-end space-x-2">
            <div>
              <span className="ml-1 text-sm">Financial Year:</span>
              <select
                value={activeYear}
                onChange={e => setActiveYear(e.target.value)}
                className="bottom-0 max-h-full w-full rounded border-transparent bg-white py-2 pl-3 text-sm outline-none ring-1 ring-gray-200 focus:border-transparent focus:ring-0"
              >
                <option value="all">All</option>
                {yearList.map(year => (
                  <option key={year.financial_year_code} value={year.financial_year_code}>
                    {year.financial_year_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <span className="ml-1 text-sm">Budget Term:</span>
              <select
                value={activeTerm}
                onChange={e => setActiveTerm(e.target.value)}
                className="bottom-0 max-h-full w-full rounded border-transparent bg-white py-2 pl-3 text-sm outline-none ring-1 ring-gray-200 focus:border-transparent focus:ring-0"
              >
                <option value="all">All</option>
                {termList.map(term => (
                  <option key={term.budget_term_id} value={term.budget_term_id}>
                    {term.budget_term_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <span className="ml-1 text-sm">Search:</span>
              <input
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Budget name, ref no…"
                className="bottom-0 max-h-full w-full rounded border-transparent bg-white py-2 pl-3 text-sm outline-none ring-1 ring-gray-200 focus:border-transparent focus:ring-0"
                type="text"
              />
            </div>
          </div>
          {/* search end */}

          <div className="flex justify-between">
            <button className="mt-1 rounded px-2 py-2 font-[Poppins] text-sm font-bold md:ml-8">IMPORT</button>
            <div className="btn-group">
              <button
                className="mt-1 rounded px-2 py-2 font-[Poppins] text-sm font-bold md:ml-8"
                data-toggle="dropdown"
                aria-expanded="false"
              >
                EXPORT
              </button>
            </div>

            <button onClick={addNewBudget} className="mr-1 mt-1 rounded bg-buttonColor px-3 py-3 font-[Poppins] text-xs text-white md:ml-8">
              ADD NEW
            </button>
          </div>
        </section>

        <section className="mt-5">
          {!tableData?.length && <DelgroProgressBar />}

          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredData}
            allowCheckBox={true} // show checkBox for each row
            setSelectedRows={selectedTableItems}
            rowKey={record => record.budget_ref_no} // custom key
            handleRowClick={OnItemCellClick}
          />

          {filteredData?.length > 0 && (
            <div className="mt-1 flex justify-end rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-md">
              {/* Total Amount: {formatCustomNumber(totalAmount)} */}
            </div>
          )}
        </section>
      </section>
    </>
  );
}

export default BudgetMainPage;
